import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * นับจำนวน request ที่กำลังทำงานด้วย ref แทน boolean
 * ของเดิมใช้ setLoading(true/false) ตรง ๆ ถ้ามี request ซ้อนกันหรือ provider ถูก
 * unmount ระหว่างทาง  stopLoading จะไปลงที่ provider ตัวเก่า ทำให้ overlay สีขาวค้าง
 */
const useLoading = () => {
  const [loading, setLoading] = useState(false);

  const countRef = useRef(0);
  const ignoreRef = useRef(false);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const apply = useCallback(() => {
    if (!mountedRef.current) return;
    setLoading(countRef.current > 0 && !ignoreRef.current);
  }, []);

  const startLoading = useCallback(() => {
    countRef.current += 1;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    apply();
  }, [apply]);

  const stopLoading = useCallback(() => {
    countRef.current = Math.max(0, countRef.current - 1);
    return new Promise((r) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        apply();
        r(countRef.current > 0);
      }, 400);
    });
  }, [apply]);

  const ignoreLoading = useCallback((bol) => {
    ignoreRef.current = !!bol;
    apply();
  }, [apply]);

  // กันค้าง: ถ้า overlay ยังขึ้นอยู่เกิน 20 วินาที ให้ปลดออกเอง จะได้ไม่ต้องกด F5
  useEffect(() => {
    if (!loading) return undefined;
    const t = setTimeout(() => {
      countRef.current = 0;
      if (mountedRef.current) setLoading(false);
    }, 20000);
    return () => clearTimeout(t);
  }, [loading]);

  return {
    loading,
    startLoading,
    stopLoading,
    ignoreLoading,
  };
};

export default useLoading;
